import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { LoginComponent } from './login/login.component';
import { MainComponent } from './main/main.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppConfigService } from './app-config.service';
import { AppHttpInterceptor } from './AppHttpInterceptor';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { UsersComponent } from './main/settings/users/users.component';
import { UserAddComponent } from './main/settings/users/add/add.component';
import { UserDetailComponent } from './main/settings/users/detail/detail.component';
import { MajorsComponent } from './main/settings/majors/majors.component';
import { MajorDetailComponent } from './main/settings/majors/detail/detail.component';
import { SubjectsComponent } from './main/settings/subjects/subjects.component';
import { ClassesComponent } from './main/settings/classes/classes.component';
import { StudentsComponent } from './main/settings/students/students.component';
import { StudentFormComponent } from './main/settings/students/form/student-form.component';
import { StudentProfileComponent } from './main/settings/students/profile/student-profile.component';
import { ParentsComponent } from './main/settings/parents/parents.component';
import { ParentProfileComponent } from './main/settings/parents/profile/parent-profile.component';
import { BusesComponent } from './main/settings/buses/buses.component';
import { BusDetailComponent } from './main/settings/buses/detail/bus-detail.component';
import { AcademicYearsComponent } from './main/settings/academic-years/academic-years.component';
import { WeeksComponent } from './main/settings/weeks/weeks.component';
import { SchoolSettingsComponent } from './main/settings/school-settings/school-settings.component';
import { AnnouncementsComponent } from './main/settings/announcements/announcements.component';
import { TeacherAnnouncementsComponent } from './main/announcements/teacher-announcements.component';
import { WeeklyPlansComponent } from './main/weekly-plans/weekly-plans.component';
import { FillDashboardComponent } from './main/dashboard/fill-dashboard.component';
import { AttendanceDashboardComponent } from './main/dashboard/attendance-dashboard.component';
import { AttendanceScannerComponent } from './main/attendance/attendance-scanner.component';
import { ManualAttendanceComponent } from './main/attendance/manual-attendance.component';
import { PickupBoardComponent } from './main/pickup/pickup-board.component';
import { PickupScannerComponent } from './main/pickup/pickup-scanner.component';
import { HeaderComponent } from './header/header.component';
import { ErrorComponent } from './modals/error/error.component';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BidiModule } from '@angular/cdk/bidi';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CanActivateRouteGuard } from './can-activate-route-guard';
import { AuthService } from './services/auth.service';
import { environment } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FooterComponent } from './footer/footer.component';
import { SearchableSelectComponent } from './shared/searchable-select/searchable-select.component';
import { AccessDirective } from './shared/access.directive';

export function HttpLoaderFactory(httpClient: HttpClient) {
  // Cache-bust so newly added keys are picked up after deploy / hard refresh.
  return new TranslateHttpLoader(httpClient, `${environment.baseHref}assets/i18n/`, '.json?v=20261007absentall');
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    MainComponent,
    UsersComponent,
    UserAddComponent,
    UserDetailComponent,
    MajorsComponent,
    MajorDetailComponent,
    SubjectsComponent,
    ClassesComponent,
    StudentsComponent,
    StudentFormComponent,
    StudentProfileComponent,
    ParentsComponent,
    ParentProfileComponent,
    BusesComponent,
    BusDetailComponent,
    AcademicYearsComponent,
    WeeksComponent,
    SchoolSettingsComponent,
    AnnouncementsComponent,
    TeacherAnnouncementsComponent,
    WeeklyPlansComponent,
    FillDashboardComponent,
    AttendanceDashboardComponent,
    AttendanceScannerComponent,
    ManualAttendanceComponent,
    PickupBoardComponent,
    PickupScannerComponent,
    HeaderComponent,
    ErrorComponent,
    FooterComponent,
    SearchableSelectComponent,
    AccessDirective
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    MatDialogModule,
    ReactiveFormsModule,
    AppRoutingModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatSelectModule,
    MatMenuModule,
    MatCheckboxModule,
    MatTooltipModule,
    BidiModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'ar'
    })
  ],
  providers: [
    AuthService,
    CanActivateRouteGuard,
    MatSnackBar,
    provideHttpClient(withInterceptors([AppHttpInterceptor])),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AppConfigService],
      useFactory: (appConfigService: AppConfigService) => {
        return async () => {
          await appConfigService.loadAppConfig();
        };
      }
    },
    provideAnimationsAsync()
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
